import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import '../simplified-finance.css';

type Props = { userId: string };

type RentalSource = {
  id: string;
  name: string;
  property_name: string | null;
  expected_amount: number;
  expected_day: number;
};

type RentalReceipt = {
  id: string;
  rental_source_id: string;
  expected_amount: number;
  received_amount: number;
  expected_date: string | null;
  received_on: string | null;
  status: string;
};

type Contribution = {
  id: string;
  contributor_label: string;
  contributed_on: string;
  amount: number;
};

type Plan = {
  juan_planned: number;
  ximena_planned: number;
  juan_due_on: string | null;
  ximena_due_on: string | null;
  notes: string | null;
};

type Installment = {
  scheduled_total: number;
  actual_charge: number | null;
};

type Remainder = {
  reimbursed_to_creditor: number;
  status: string;
};

type Service = { id: string; default_amount: number | null; is_active: boolean };
type ServicePayment = {
  service_id: string;
  expected_amount: number | null;
  actual_amount: number | null;
  status: string;
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function monthBounds(month: string) {
  const [year, monthNumber] = month.split('-').map(Number);
  return {
    start: `${month}-01`,
    end: new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10),
  };
}

function safeDate(month: string, day: number) {
  const { end } = monthBounds(month);
  const maxDay = Number(end.slice(-2));
  return `${month}-${String(Math.min(Math.max(day, 1), maxDay)).padStart(2, '0')}`;
}

export default function HouseholdContributions({ userId }: Props) {
  const [month, setMonth] = useState(currentMonth());
  const [rentals, setRentals] = useState<RentalSource[]>([]);
  const [receipts, setReceipts] = useState<RentalReceipt[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [installment, setInstallment] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [servicesExpected, setServicesExpected] = useState(0);
  const [juanPlanned, setJuanPlanned] = useState('0');
  const [ximenaPlanned, setXimenaPlanned] = useState('0');
  const [juanDueOn, setJuanDueOn] = useState('');
  const [ximenaDueOn, setXimenaDueOn] = useState('');
  const [notes, setNotes] = useState('');
  const [rentSourceId, setRentSourceId] = useState('');
  const [rentDate, setRentDate] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [contributor, setContributor] = useState<'Juan Carlos' | 'Ximena'>('Juan Carlos');
  const [contributionDate, setContributionDate] = useState('');
  const [contributionAmount, setContributionAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function loadData(clearMessage = false) {
    setLoading(true);
    if (clearMessage) setMessage('');
    const { start, end } = monthBounds(month);
    const periodMonth = `${month}-01`;
    const [installmentResult, remainderResult, rentalResult, receiptResult, contributionResult, serviceResult, paymentResult, planResult] = await Promise.all([
      supabase.from('mortgage_installments_public').select('scheduled_total, actual_charge').gte('due_date', start).lte('due_date', end),
      supabase.from('reimbursement_remainders_public').select('reimbursed_to_creditor, status').eq('period_month', periodMonth).neq('status', 'cancelled'),
      supabase.from('rental_sources').select('id, name, property_name, expected_amount, expected_day').eq('is_active', true).order('expected_day'),
      supabase.from('rental_receipts').select('id, rental_source_id, expected_amount, received_amount, expected_date, received_on, status').eq('period_month', periodMonth).neq('status', 'cancelled'),
      supabase.from('household_contributions').select('id, contributor_label, contributed_on, amount').eq('record_status', 'active').gte('contributed_on', start).lte('contributed_on', end).order('contributed_on'),
      supabase.from('shared_services').select('id, default_amount, is_active').eq('is_active', true),
      supabase.from('shared_service_payments').select('service_id, expected_amount, actual_amount, status').eq('period_month', periodMonth).neq('status', 'cancelled'),
      supabase.from('monthly_household_plans').select('juan_planned, ximena_planned, juan_due_on, ximena_due_on, notes').eq('period_month', periodMonth).maybeSingle(),
    ]);

    const error = [installmentResult.error, remainderResult.error, rentalResult.error, receiptResult.error, contributionResult.error, serviceResult.error, paymentResult.error, planResult.error].find(Boolean);
    if (error) {
      setMessage('No fue posible cargar la distribución del periodo.');
      setLoading(false);
      return;
    }

    const loadedRentals = (rentalResult.data ?? []) as RentalSource[];
    const loadedServices = (serviceResult.data ?? []) as Service[];
    const loadedPayments = (paymentResult.data ?? []) as ServicePayment[];
    const servicePaymentMap = new Map(loadedPayments.map((payment) => [payment.service_id, payment]));
    const serviceTotal = loadedServices.reduce((total, service) => {
      const payment = servicePaymentMap.get(service.id);
      return total + Number(payment?.actual_amount ?? payment?.expected_amount ?? service.default_amount ?? 0);
    }, 0);

    setInstallment((installmentResult.data ?? []).reduce((total, row) => total + Number((row as Installment).actual_charge ?? (row as Installment).scheduled_total ?? 0), 0));
    setAdvance((remainderResult.data ?? []).reduce((total, row) => total + Number((row as Remainder).reimbursed_to_creditor ?? 0), 0));
    setRentals(loadedRentals);
    setReceipts((receiptResult.data ?? []) as RentalReceipt[]);
    setContributions((contributionResult.data ?? []) as Contribution[]);
    setServicesExpected(serviceTotal);

    const plan = planResult.data as Plan | null;
    setJuanPlanned(String(plan?.juan_planned ?? 0));
    setXimenaPlanned(String(plan?.ximena_planned ?? 0));
    setJuanDueOn(plan?.juan_due_on ?? '');
    setXimenaDueOn(plan?.ximena_due_on ?? '');
    setNotes(plan?.notes ?? '');

    if (loadedRentals.length) {
      const first = loadedRentals[0];
      const existing = (receiptResult.data ?? []).find((item) => item.rental_source_id === first.id) as RentalReceipt | undefined;
      setRentSourceId(first.id);
      setRentDate(existing?.received_on ?? safeDate(month, first.expected_day));
      setRentAmount(String(existing?.received_amount ?? first.expected_amount));
    }
    setContributionDate(`${month}-01`);
    setLoading(false);
  }

  useEffect(() => {
    void loadData(true);
  }, [month]);

  const receiptMap = useMemo(
    () => new Map(receipts.map((receipt) => [receipt.rental_source_id, receipt])),
    [receipts],
  );

  const calculation = useMemo(() => {
    const rentExpected = rentals.reduce((total, item) => total + Number(item.expected_amount ?? 0), 0);
    const rentReceived = receipts.reduce((total, item) => total + Number(item.received_amount ?? 0), 0);
    const juanActual = contributions.filter((item) => item.contributor_label.toLowerCase().includes('juan')).reduce((total, item) => total + Number(item.amount), 0);
    const ximenaActual = contributions.filter((item) => item.contributor_label.toLowerCase().includes('ximena')).reduce((total, item) => total + Number(item.amount), 0);
    const juanPlan = Math.max(0, Number(juanPlanned) || 0);
    const ximenaPlan = Math.max(0, Number(ximenaPlanned) || 0);
    const obligations = installment + servicesExpected;
    const actualResources = advance + rentReceived + juanActual + ximenaActual;
    const expectedResources = advance + rentExpected + juanPlan + ximenaPlan;
    return {
      rentExpected,
      rentReceived,
      juanActual,
      ximenaActual,
      juanPlan,
      ximenaPlan,
      obligations,
      actualResources,
      expectedResources,
      actualGap: Math.max(0, obligations - actualResources),
      expectedGap: Math.max(0, obligations - expectedResources),
    };
  }, [advance, contributions, installment, juanPlanned, receipts, rentals, servicesExpected, ximenaPlanned]);

  async function savePlan(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const { error } = await supabase.from('monthly_household_plans').upsert({
      period_month: `${month}-01`,
      mortgage_target: installment,
      rent_expected: calculation.rentExpected,
      services_expected: servicesExpected,
      other_shared_expected: 0,
      joint_fund_planned: advance,
      juan_planned: calculation.juanPlan,
      ximena_planned: calculation.ximenaPlan,
      juan_due_on: juanDueOn || null,
      ximena_due_on: ximenaDueOn || null,
      notes: notes.trim() || null,
      created_by: userId,
      updated_by: userId,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'period_month' });
    setMessage(error ? 'No fue posible guardar la distribución.' : 'Aportaciones planeadas actualizadas.');
    setBusy(false);
  }

  function chooseRental(id: string) {
    setRentSourceId(id);
    const rental = rentals.find((item) => item.id === id);
    const receipt = receiptMap.get(id);
    setRentDate(receipt?.received_on ?? safeDate(month, rental?.expected_day ?? 1));
    setRentAmount(String(receipt?.received_amount ?? rental?.expected_amount ?? ''));
  }

  async function saveRent(event: FormEvent) {
    event.preventDefault();
    const rental = rentals.find((item) => item.id === rentSourceId);
    const amount = Number(rentAmount);
    if (!rental || !rentDate || !Number.isFinite(amount) || amount < 0) {
      setMessage('Completa la renta, fecha e importe.');
      return;
    }
    setBusy(true);
    const existing = receiptMap.get(rentSourceId);
    const payload = {
      rental_source_id: rentSourceId,
      period_month: `${month}-01`,
      expected_date: safeDate(month, rental.expected_day),
      received_on: rentDate,
      expected_amount: rental.expected_amount,
      received_amount: amount,
      status: amount >= rental.expected_amount ? 'received' : 'partial',
      notes: 'Registro simplificado de renta',
      updated_at: new Date().toISOString(),
    };
    const result = existing
      ? await supabase.from('rental_receipts').update(payload).eq('id', existing.id)
      : await supabase.from('rental_receipts').insert(payload);
    setMessage(result.error ? 'No fue posible registrar la renta.' : 'Renta actualizada.');
    if (!result.error) await loadData();
    setBusy(false);
  }

  async function saveContribution(event: FormEvent) {
    event.preventDefault();
    const amount = Number(contributionAmount);
    if (!contributionDate || !Number.isFinite(amount) || amount <= 0) {
      setMessage('Completa la fecha y el importe de la aportación.');
      return;
    }
    setBusy(true);
    const { error } = await supabase.from('household_contributions').insert({
      contributor_label: contributor,
      contributed_on: contributionDate,
      amount,
      purpose: 'Cobertura mensual del proyecto',
      notes: 'Registro simplificado de aportación',
    });
    setMessage(error ? 'No fue posible registrar la aportación.' : 'Aportación registrada.');
    if (!error) {
      setContributionAmount('');
      await loadData();
    }
    setBusy(false);
  }

  return (
    <section className="simple-page">
      <div className="simple-heading">
        <div>
          <p>Cobertura mensual</p>
          <h1>Aportaciones y rentas</h1>
          <span>Define cuánto aporta cada uno, registra las rentas y consulta cuánto falta para cubrir hipoteca y servicios.</span>
        </div>
        <label>Periodo<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
      </div>

      {loading && <div className="simple-state">Calculando la cobertura…</div>}
      {!loading && (
        <>
          <section className="simple-summary simple-summary--four">
            <article><span>Hipoteca</span><strong>{currency.format(installment)}</strong><small>{currency.format(advance)} ya anticipados</small></article>
            <article><span>Servicios</span><strong>{currency.format(servicesExpected)}</strong><small>Estimado o registrado</small></article>
            <article><span>Recursos previstos</span><strong>{currency.format(calculation.expectedResources)}</strong><small>Rentas + aportaciones + anticipo</small></article>
            <article className={calculation.expectedGap > 0 ? 'is-warning' : 'is-positive'}><span>Faltante previsto</span><strong>{currency.format(calculation.expectedGap)}</strong><small>{calculation.expectedGap > 0 ? 'Requiere ajuste' : 'Cobertura completa'}</small></article>
          </section>

          <div className="simple-two-columns">
            <form className="simple-card simple-form" onSubmit={savePlan}>
              <div className="simple-card-heading"><div><small>Acuerdo mensual</small><h2>¿Cuánto aporta cada uno?</h2></div></div>
              <div className="simple-grid">
                <label>Juan Carlos<input type="number" min="0" step="0.01" value={juanPlanned} onChange={(event) => setJuanPlanned(event.target.value)} /></label>
                <label>Fecha prevista<input type="date" value={juanDueOn} onChange={(event) => setJuanDueOn(event.target.value)} /></label>
                <label>Ximena<input type="number" min="0" step="0.01" value={ximenaPlanned} onChange={(event) => setXimenaPlanned(event.target.value)} /></label>
                <label>Fecha prevista<input type="date" value={ximenaDueOn} onChange={(event) => setXimenaDueOn(event.target.value)} /></label>
                <label className="simple-wide">Notas<textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
              </div>
              <button className="simple-primary" type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Guardar distribución'}</button>
              <div className="simple-breakdown"><div><span>Juan planeado / real</span><b>{currency.format(calculation.juanPlan)} / {currency.format(calculation.juanActual)}</b></div><div><span>Ximena planeado / real</span><b>{currency.format(calculation.ximenaPlan)} / {currency.format(calculation.ximenaActual)}</b></div></div>
            </form>

            <aside className="simple-card simple-result">
              <small>Resultado del periodo</small>
              <h2>{calculation.expectedGap > 0 ? 'Cobertura pendiente' : 'Cobertura planeada'}</h2>
              <strong>{currency.format(calculation.expectedGap)}</strong>
              <div><span>Obligaciones</span><b>{currency.format(calculation.obligations)}</b></div>
              <div><span>Ya recibido o aplicado</span><b>{currency.format(calculation.actualResources)}</b></div>
              <div><span>Faltante con recursos reales</span><b>{currency.format(calculation.actualGap)}</b></div>
              <div><span>Recursos esperados</span><b>{currency.format(calculation.expectedResources)}</b></div>
            </aside>
          </div>

          <section className="simple-card">
            <div className="simple-card-heading"><div><small>Ingresos del inmueble</small><h2>Rentas del periodo</h2></div><strong>{currency.format(calculation.rentReceived)} / {currency.format(calculation.rentExpected)}</strong></div>
            <div className="simple-rent-list">
              {rentals.map((rental) => {
                const receipt = receiptMap.get(rental.id);
                return <article key={rental.id}><div><strong>{rental.name}</strong><span>{rental.property_name ?? 'Inmueble'} · día {rental.expected_day}</span></div><div><b>{currency.format(receipt?.received_amount ?? 0)}</b><small>de {currency.format(rental.expected_amount)}</small></div></article>;
              })}
            </div>
            <form className="simple-inline-form" onSubmit={saveRent}>
              <label>Renta<select value={rentSourceId} onChange={(event) => chooseRental(event.target.value)}>{rentals.map((rental) => <option key={rental.id} value={rental.id}>{rental.name}</option>)}</select></label>
              <label>Fecha<input type="date" value={rentDate} onChange={(event) => setRentDate(event.target.value)} /></label>
              <label>Importe<input type="number" min="0" step="0.01" value={rentAmount} onChange={(event) => setRentAmount(event.target.value)} /></label>
              <button className="simple-primary" type="submit" disabled={busy || !rentSourceId}>Guardar renta</button>
            </form>
          </section>

          <section className="simple-card">
            <div className="simple-card-heading"><div><small>Registro real</small><h2>Aportaciones entregadas</h2></div><strong>{currency.format(calculation.juanActual + calculation.ximenaActual)}</strong></div>
            <div className="simple-contribution-list">
              {contributions.map((item) => <article key={item.id}><div><strong>{item.contributor_label}</strong><span>{item.contributed_on}</span></div><b>{currency.format(item.amount)}</b></article>)}
              {!contributions.length && <p className="simple-empty">Aún no hay aportaciones reales registradas en este mes.</p>}
            </div>
            <form className="simple-inline-form" onSubmit={saveContribution}>
              <label>Persona<select value={contributor} onChange={(event) => setContributor(event.target.value as 'Juan Carlos' | 'Ximena')}><option value="Juan Carlos">Juan Carlos</option><option value="Ximena">Ximena</option></select></label>
              <label>Fecha<input type="date" value={contributionDate} onChange={(event) => setContributionDate(event.target.value)} /></label>
              <label>Importe<input type="number" min="0.01" step="0.01" value={contributionAmount} onChange={(event) => setContributionAmount(event.target.value)} /></label>
              <button className="simple-primary" type="submit" disabled={busy}>Registrar aportación</button>
            </form>
          </section>
        </>
      )}

      {message && <p className="simple-message" role="status">{message}</p>}
    </section>
  );
}
